import { BlockOutlined } from '@mui/icons-material'
import { Box, Tab, Tabs } from '@mui/material'
import type { FC, ReactElement } from 'react'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TabPanel from '@/components/layouts/TabPanel'
import { useAlertContext } from '@/contexts/AlertContext'
import type { ActivationGroupUuid } from '@/features/activationGroup/types'
import PriceDataGridForActivationGroup from '@/features/bidding/components/capacity/offer/PriceDataGridForActivationGroup'
import { useResetPtusContext } from '@/features/bidding/contexts/ResetPtusContext'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidHistoriesPage } from '@/features/bidding/types/bidHistory'
import { convertToRoundedMw } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { checkIfActivationGroupHasBidsForTheDay } from '@/features/bidding/utils/price/checkIfActivationGroupHasBidsForTheDay'
import type { PriceQuantity } from '@/features/bidding/utils/price/getPtuPriceQuantitiesFromBids'
import { recalculateRemaining } from '@/features/bidding/utils/price/pastedBid'
import { findExpiredPtuPrefillSource } from '@/features/bidding/utils/ptuPrefillSources'
import { validateAggregatedBid } from '@/features/bidding/utils/validation/aggregatedBidValidations'

type Props = {
  bidHistoriesPage: BidHistoriesPage | undefined
  groupParams: GroupedBidParams
  bidsPerActivationGroup: Map<ActivationGroupUuid, AggregatedBidWrapper>
  latestNonDraftBidsByActivationGroup: Map<string, ExtendedBid | null>
  latestAcceptedBidsByActivationGroup: Map<string, ExtendedBid | null>
  onBidForActivationGroupUpdated: (updatedBid: AggregatedBidWrapper) => void
  tabIcons: Map<ActivationGroupUuid, ReactElement | undefined>
}

const BidOfferPriceByActivationGroupTabs: FC<Props> = memo(function BidOfferPriceTabs({
  bidHistoriesPage,
  groupParams,
  bidsPerActivationGroup,
  latestNonDraftBidsByActivationGroup,
  latestAcceptedBidsByActivationGroup,
  onBidForActivationGroupUpdated,
  tabIcons,
}) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { markPtuAsReset } = useResetPtusContext()
  const [activeTab, setActiveTab] = useState(getFirstTabIndexThatHasOfferedVolume(bidsPerActivationGroup))

  const { isPtuEditable, currentTime } = usePtuExpiration()

  // Update aggregated bids for expired PTUs (initial load & real-time)
  useEffect(() => {
    for (const bidWrapper of bidsPerActivationGroup.values()) {
      // Expired PTUs follow the accepted version, falling back to the latest sent one (BID-209)
      const acceptedBidForGroup = latestAcceptedBidsByActivationGroup.get(bidWrapper.activationGroup.uuid)
      const latestBidForGroup = latestNonDraftBidsByActivationGroup.get(bidWrapper.activationGroup.uuid)

      const updatedPtus = bidWrapper.aggregatedBid.ptus.map((ptu) => {
        const editable = isPtuEditable(ptu.ptuStart, groupParams.bidType, groupParams.marketProgram)
        if (editable) return ptu

        // Determine expected price quantities & available volume for expired PTUs
        let expectedPriceQuantities: PriceQuantity[]
        let expectedAvailable: number

        const matchingPtu = findExpiredPtuPrefillSource(ptu.ptuStart, acceptedBidForGroup, latestBidForGroup)

        if (matchingPtu) {
          // Volume and ladder are taken from the same version, so they cannot disagree (BID-252).
          // The submitted volume must equal the source PTU's offeredVolume, since that is the
          // field Portfolio Manager diffs against its baseline, at both PTU and chunk level.
          expectedPriceQuantities =
            matchingPtu.ptuChunks?.map((chunk) => ({
              price: chunk.offeredPrice,
              quantity: convertToRoundedMw(chunk.offeredVolume.quantity),
            })) ?? []
          expectedAvailable = convertToRoundedMw(matchingPtu.offeredVolume.quantity)
        } else {
          // New bid, or no version covers this PTU - set everything to 0
          expectedPriceQuantities = ptu.priceQuantities.map((pq) => ({ ...pq, quantity: 0 }))
          expectedAvailable = 0
        }

        // Ensure all existing price columns are present; if any are missing, set their quantity to 0
        const existingPrices = new Set(expectedPriceQuantities.map((pq) => pq.price))
        for (const pq of ptu.priceQuantities) {
          if (!existingPrices.has(pq.price)) {
            expectedPriceQuantities.push({ price: pq.price, quantity: 0 })
          }
        }

        // Check if anything changed (price quantities or available volume)
        const changed =
          expectedAvailable !== ptu.available ||
          expectedPriceQuantities.length !== ptu.priceQuantities.length ||
          expectedPriceQuantities.some((eq, idx) => eq.quantity !== ptu.priceQuantities[idx]?.quantity)

        if (!changed) return ptu

        // Try to mark this PTU as reset, only succeeds if PTU expired during this session
        const wasMarkedAsReset = markPtuAsReset({
          activationGroupUuid: bidWrapper.activationGroup.uuid,
          ptuStart: ptu.ptuStart,
          bidType: groupParams.bidType,
          marketProgram: groupParams.marketProgram,
        })
        if (wasMarkedAsReset) {
          pushAlert({
            severity: 'warning',
            message: t('bidding.create_bid.ptu_reset_alert.message', {
              ptu: ptu.ptuStart.toFormat('HH:mm'),
              activationGroup: bidWrapper.activationGroup.code,
            }),
            title: t('bidding.create_bid.ptu_reset_alert.title', { ptu: ptu.ptuStart.toFormat('HH:mm') }),
          })
        }

        // Create a new PTU object with updated values
        const newPtu = { ...ptu, priceQuantities: expectedPriceQuantities, available: expectedAvailable }
        // Recalculate remaining volume so that the "Remaining" column is updated correctly
        recalculateRemaining(newPtu, bidWrapper.aggregatedBid.ladderType)
        return newPtu
      })

      // Compare arrays shallowly to detect any update
      const ptusChanged = updatedPtus.some((ptu, idx) => ptu !== bidWrapper.aggregatedBid.ptus[idx])
      if (ptusChanged) {
        const newAggregatedBid = { ...bidWrapper.aggregatedBid, ptus: updatedPtus }
        onBidForActivationGroupUpdated({
          activationGroup: bidWrapper.activationGroup,
          aggregatedBid: newAggregatedBid,
          state: validateAggregatedBid(
            newAggregatedBid,
            bidWrapper.activationGroup,
            bidsPerActivationGroup,
            groupParams.bidType,
          ),
        })
      }
    }
  }, [
    currentTime.toMillis(),
    latestNonDraftBidsByActivationGroup,
    latestAcceptedBidsByActivationGroup,
    bidsPerActivationGroup,
    groupParams,
    markPtuAsReset,
  ])

  if (!bidsPerActivationGroup?.size) {
    return null
  }

  if (bidsPerActivationGroup.size === 1) {
    const singleBid = bidsPerActivationGroup.entries().next().value?.[1]
    return (
      <Box>
        <PriceDataGridForActivationGroup
          activationGroup={singleBid!.activationGroup}
          bidsPerActivationGroup={bidsPerActivationGroup}
          groupParams={groupParams}
          hasAlreadyBidsForTheDay={checkIfActivationGroupHasBidsForTheDay(
            bidHistoriesPage,
            singleBid!.activationGroup.uuid,
          )}
          onBidForActivationGroupUpdated={onBidForActivationGroupUpdated}
        />
      </Box>
    )
  }

  const sortedActivationGroups = Array.from(bidsPerActivationGroup.entries())
    .map((entry) => entry[1].activationGroup)
    .toSorted((a, b) => a.code.localeCompare(b.code))

  return (
    <>
      <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          scrollButtons="auto"
          style={{ minHeight: 0 }}
          value={activeTab}
          variant="scrollable"
          onChange={(_, newValue) => setActiveTab(newValue)}
        >
          {sortedActivationGroups.map((activationGroup, index) => {
            const isDisabled = hasNotOfferedVolume(bidsPerActivationGroup.get(activationGroup.uuid)!.aggregatedBid)

            return (
              <Tab
                key={activationGroup.uuid}
                disabled={isDisabled}
                icon={isDisabled ? <BlockOutlined /> : getTabIcon(tabIcons, activationGroup.uuid)}
                iconPosition="start"
                label={activationGroup.code}
                style={{ minHeight: 0 }}
                value={index}
              />
            )
          })}
        </Tabs>
      </Box>

      {sortedActivationGroups.map((activationGroup, index) => (
        <TabPanel key={activationGroup.uuid} index={index} value={activeTab}>
          <PriceDataGridForActivationGroup
            activationGroup={activationGroup}
            bidsPerActivationGroup={bidsPerActivationGroup}
            groupParams={groupParams}
            hasAlreadyBidsForTheDay={checkIfActivationGroupHasBidsForTheDay(bidHistoriesPage, activationGroup.uuid)}
            onBidForActivationGroupUpdated={onBidForActivationGroupUpdated}
          />
        </TabPanel>
      ))}
    </>
  )
})

function getFirstTabIndexThatHasOfferedVolume(
  bidsPerActivationGroup: Map<ActivationGroupUuid, AggregatedBidWrapper>,
): number {
  for (const [index, bidWrapper] of Array.from(bidsPerActivationGroup.entries()).entries()) {
    if (!hasNotOfferedVolume(bidWrapper[1].aggregatedBid)) {
      return index
    }
  }

  return 0 // Default to the first tab if all have no offered volume
}

function hasNotOfferedVolume(aggregatedBid: AggregatedBidWrapper['aggregatedBid']): boolean {
  return aggregatedBid.ptus.every((ptu) => ptu.available === 0)
}

const getTabIcon = (
  tabIcons: Map<ActivationGroupUuid, ReactElement | undefined>,
  activationGroupUuid: ActivationGroupUuid,
): ReactElement | undefined => {
  return tabIcons.get(activationGroupUuid)
}

export default BidOfferPriceByActivationGroupTabs
